/**
 * معاملات اهرمی — رابط کاربری
 * ==============================
 * قرارداد تعهدیِ داخلی با وثیقه **ایزوله**: کاربر وثیقه می‌گذارد، موقعیتی
 * چند برابرِ آن باز می‌کند و در پایان فقط اختلاف قیمت تسویه می‌شود.
 *
 * سه تصمیمِ رابط که عمدی‌اند:
 *
 * ۱. **قیمت لیکویید پیش از ثبت سفارش دیده می‌شود، نه بعد از آن.** تنها عددی
 *    که در معامله اهرمی واقعاً اهمیت دارد همین است؛ اگر کاربر آن را بعد از
 *    باز کردن موقعیت ببیند، دیر است.
 * ۲. **سود و زیان زنده در خودِ مرورگر حساب می‌شود** — از قیمتِ همان
 *    وب‌سوکتی که بقیه صفحه را زنده نگه می‌دارد. نه درخواست تازه‌ای اضافه
 *    می‌شود، نه عدد روی صفحه یخ می‌زند.
 * ۳. **وقتی در تنظیمات خاموش است، هیچ ردی از این بخش دیده نمی‌شود** — نه
 *    زبانه‌ای، نه دکمه‌ای. کاربر نباید چیزی ببیند که نمی‌تواند استفاده کند.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Info, Plus, ShieldAlert } from 'lucide-react'
import { Button, Card, Modal, NumberInput, Tabs, Empty, Rows, Badge } from './UI'
import { get, post } from '../lib/api'
import { clsx, fa, num, faDate } from '../lib/format'
import { useAuth, useMarket, toast } from '../store'
import {
  LevConfig, Position, Side, LONG, SHORT,
  previewOpen, validateOpen, checkTargets, pnlOf, roeOf, leverageSteps,
  closeReasonLabel, isLiquidated,
} from '../lib/leverage'

const unit = (q: string) => (q === 'IRT' ? 'تومان' : q)
const dg = (q: string) => (q === 'IRT' ? 0 : 4)

/* ═══════════════════════════════════════════════════ تنظیمات */

/**
 * تنظیمات یک‌بار برای کل برنامه گرفته می‌شود.
 *
 * بدون این حافظه، هر کامپوننتی که به اهرم مربوط است یک درخواست جدا
 * می‌فرستاد — و چون چند تای آن‌ها هم‌زمان روی صفحه معامله هستند، کاربر
 * برای یک عدد ثابت سه بار انتظار می‌کشید.
 */
let cfgCache: Promise<LevConfig> | null = null
let cfgToken: string | null = null

export function resetLeverageConfig() { cfgCache = null; cfgToken = null }

export function useLeverageConfig(): { cfg: LevConfig | null; loading: boolean } {
  const { user } = useAuth()
  const [cfg, setCfg] = useState<LevConfig | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    // با عوض‌شدن کاربر باید دوباره گرفته شود: `kycOk` به خودِ کاربر بستگی
    // دارد و اگر حافظه را نگه می‌داشتیم، کاربر تازه‌وارد پاسخِ نفر قبلی را
    // می‌دید.
    const key = user ? String(user.id ?? user.email ?? '1') : ''
    if (!cfgCache || cfgToken !== key) {
      cfgToken = key
      cfgCache = get<LevConfig>('/leverage/config')
    }
    setLoading(true)
    const mine = cfgCache
    mine
      .then((r) => { if (alive) { setCfg(r); setLoading(false) } })
      .catch(() => {
        // حافظه پاک می‌شود، وگرنه همان وعده‌ی ردشده تا پایان نشست دوباره
        // و دوباره برگردانده می‌شد و یک قطعیِ لحظه‌ای، کاربری را که موقعیت
        // باز دارد تا رفرش صفحه بی‌دست‌وپا می‌کرد.
        if (cfgCache === mine) { cfgCache = null; cfgToken = null }
        if (alive) { setCfg(null); setLoading(false) }
      })
    return () => { alive = false }
  }, [user])

  return { cfg, loading }
}

/** آیا این بازار اهرم دارد؟ */
export const leverageOn = (cfg: LevConfig | null, symbol: string) =>
  !!cfg?.enabled && (!cfg.markets.length || cfg.markets.includes(symbol.toUpperCase()))

/* ═══════════════════════════════════════════════════ هشدار ریسک */

export function RiskNote({ cfg }: { cfg: LevConfig }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-xl border border-amber-500/25 bg-amber-500/[.06] p-3 text-[11px] leading-6 text-amber-200/90">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 text-right font-bold">
        <ShieldAlert className="h-4 w-4 shrink-0" />
        <span>معامله اهرمی پرریسک است — پیش از شروع بخوانید</span>
        <Info className={clsx('ms-auto h-3.5 w-3.5 transition', open && 'rotate-180')} />
      </button>
      {open && (
        <ul className="mt-2 list-inside list-disc space-y-1 text-amber-100/70">
          <li>با اهرم {fa(cfg.maxLeverage)} برابر، حرکتِ حدود {fa(num(100 / cfg.maxLeverage, 1))}٪ خلافِ جهت شما، تمام وثیقه را می‌برد.</li>
          <li>وثیقه <b>ایزوله</b> است: زیان هر موقعیت حداکثر به اندازه وثیقه همان موقعیت است و بقیه دارایی شما دست‌نخورده می‌ماند.</li>
          <li>لیکویید شدن با <b>قیمت واقعی همان لحظه</b> انجام می‌شود؛ در پرش‌های شدید بازار ممکن است چیزی از وثیقه باقی نماند.</li>
          <li>کارمزد باز و بسته کردن، هرکدام {fa(num(cfg.fee * 100, 3))}٪ ارزش کل موقعیت است — نه ارزش وثیقه.</li>
          <li>در این قرارداد ارزی خرید یا قرض گرفته نمی‌شود؛ فقط اختلاف قیمت تسویه می‌گردد.</li>
        </ul>
      )}
    </div>
  )
}

/* ═══════════════════════════════════════════════════ فرم باز کردن موقعیت */

export function LeverageForm({
  symbol, cfg, presetPrice, onDone,
}: { symbol: string; cfg: LevConfig; presetPrice?: number; onDone: () => void }) {
  const t = useMarket((s) => s.tickers[symbol])
  const usdtIrt = useMarket((s) => s.usdtIrt)
  const { user } = useAuth()
  const [side, setSide] = useState<Side>(LONG)
  // سقفِ سرور می‌تواند اعشاری باشد (مثلاً ۱٫۹۹ وقتی حاشیه نگهداری بزرگ
  // است). نوار لغزنده با گام ۱ هرگز به آن نمی‌رسد و عددِ نمایش‌داده‌شده هم
  // گرد می‌شد — کاربر «۲×» می‌دید و ۱٫۹۹ ثبت می‌شد. پس کفِ عدد صحیح مبنا
  // است؛ همیشه از سقف سرور کمتر یا برابر است، پس هرگز رد نمی‌شود.
  const maxLev = Math.max(1, Math.floor(cfg.maxLeverage))
  const [lev, setLev] = useState(Math.min(5, maxLev))
  const [margin, setMargin] = useState('')
  const [tp, setTp] = useState('')
  const [sl, setSl] = useState('')
  const [adv, setAdv] = useState(false)
  const [loading, setLoading] = useState(false)
  const [balance, setBalance] = useState(0)
  const [openCount, setOpenCount] = useState(0)

  const quote = t?.quote ?? 'IRT'
  const entry = presetPrice && presetPrice > 0 ? presetPrice : t?.last ?? 0
  const m = Number(margin) || 0

  const load = useCallback(() => {
    if (!user) return
    get('/wallet/balances').then((r: any) => {
      const row = (r.assets || []).find((a: any) => a.currency === quote)
      setBalance(row?.balance ?? 0)
    }).catch(() => {})
    get<Position[]>('/leverage/positions?status=open')
      .then((r) => setOpenCount(Array.isArray(r) ? r.length : 0)).catch(() => {})
  }, [user, quote])
  useEffect(load, [load])

  useEffect(() => { setLev((v) => Math.min(v, maxLev)) }, [maxLev])

  const pv = useMemo(() => previewOpen(cfg, side, entry, lev, m), [cfg, side, entry, lev, m])

  /** ارزش تومانیِ موقعیت — سقف‌های سرور بر همین مبنا سنجیده می‌شوند */
  const notionalIrt = quote === 'IRT' ? pv.notional : pv.notional * (usdtIrt || 0)

  // اعتبارسنجی حد سود/ضرر پیش از ارسال، نه پس از آن. بدون این، سرور
  // درخواست را می‌گرفت، وثیقه را قفل می‌کرد، خطا می‌داد و برمی‌گرداند —
  // رفت‌وبرگشتی که هیچ لازم نبود.
  const err = validateOpen(cfg, symbol, side, lev, m, {
    balance, notionalIrt, openPositions: openCount,
  }) || checkTargets(side, entry, pv.liq, Number(tp) || 0, Number(sl) || 0)
  // پیش از آنکه کاربر عددی وارد کند، خطا نشان نمی‌دهیم؛ فقط دکمه خاموش است.
  const showErr = m > 0 ? err : ''

  const setPercent = (p: number) => {
    // کارمزدِ باز کردن هم از همین موجودی برداشته می‌شود؛ اگر آن را کنار
    // نگذاریم، «۱۰۰٪» همیشه با «موجودی کافی نیست» رد می‌شود.
    const usable = (balance * p) / 100 / (1 + cfg.fee * lev)
    setMargin(usable > 0 ? String(Number(usable.toFixed(quote === 'IRT' ? 0 : 4))) : '')
  }

  const submit = async () => {
    if (!user) return toast.err('برای معامله اهرمی ابتدا وارد حساب کاربری شوید')
    if (err) return toast.err(err)
    setLoading(true)
    try {
      await post('/leverage/positions', {
        symbol, side, leverage: lev, margin: m,
        tp: Number(tp) || 0, sl: Number(sl) || 0,
      })
      toast.ok(side === LONG ? 'موقعیت خرید باز شد' : 'موقعیت فروش باز شد')
      setMargin(''); setTp(''); setSl('')
      load(); onDone()
    } catch (e: any) {
      toast.err(e.message)
    } finally {
      setLoading(false)
    }
  }

  const steps = leverageSteps(maxLev)

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {([LONG, SHORT] as Side[]).map((s) => (
          <button key={s} onClick={() => setSide(s)}
            className={clsx('rounded-xl py-2.5 text-sm font-bold transition',
              side === s
                ? s === LONG ? 'bg-gradient-to-l from-emerald-500 to-up text-ink-950 shadow-glow-up'
                             : 'bg-gradient-to-l from-rose-500 to-down text-white'
                : 'border border-white/10 bg-white/[.03] text-slate-400 hover:text-slate-200')}>
            {s === LONG ? 'خرید (Long)' : 'فروش (Short)'}
          </button>
        ))}
      </div>

      {/* ── اهرم */}
      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="label !mb-0">اهرم</label>
          <span className="num text-sm font-extrabold text-brand-200">{fa(lev)}×</span>
        </div>
        <input type="range" min={1} max={maxLev} step={1} value={lev} dir="ltr"
          onChange={(e) => setLev(Number(e.target.value))}
          className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-white/10 accent-brand-500" />
        {steps.length > 0 && (
          <div className="mt-2 grid gap-1" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0,1fr))` }}>
            {steps.map((v) => (
              <button key={v} onClick={() => setLev(v)}
                className={clsx('rounded-lg py-1 text-[11px] transition',
                  lev === v ? 'bg-brand-500/20 text-brand-200 ring-1 ring-brand-500/40'
                            : 'border border-white/[.07] bg-white/[.03] text-slate-400 hover:text-slate-200')}>
                {fa(v)}×
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── وثیقه */}
      <div>
        <label className="label">وثیقه</label>
        <NumberInput value={margin} onChange={setMargin} suffix={unit(quote)} />
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {[25, 50, 75, 100].map((v) => (
          <button key={v} onClick={() => setPercent(v)}
            className="rounded-lg border border-white/[.07] bg-white/[.03] py-1.5 text-[11px] text-slate-400 transition hover:border-brand-500/40 hover:text-brand-200">
            {fa(v)}٪
          </button>
        ))}
      </div>

      {/* ── حد سود و ضرر */}
      <button onClick={() => setAdv((a) => !a)} className="text-[11px] text-slate-500 hover:text-slate-300">
        {adv ? '−' : '+'} حد سود و حد ضرر (اختیاری)
      </button>
      {adv && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label !text-[10px]">حد سود</label>
            <NumberInput value={tp} onChange={setTp} placeholder="—" />
          </div>
          <div>
            <label className="label !text-[10px]">حد ضرر</label>
            <NumberInput value={sl} onChange={setSl} placeholder="—" />
          </div>
        </div>
      )}

      {/* ── پیش‌نمایش */}
      <div className="space-y-1.5 rounded-xl border border-white/[.06] bg-ink-900/50 p-3 text-[11px]">
        <Row k="موجودی قابل استفاده" v={`${num(balance, dg(quote))} ${unit(quote)}`} />
        <Row k="ارزش کل موقعیت" v={`${num(pv.notional, dg(quote))} ${unit(quote)}`} strong />
        <Row k={`اندازه موقعیت`} v={`${num(pv.size, 6)} ${t?.base ?? ''}`} />
        <Row k="قیمت ورود" v={`${num(entry, dg(quote))} ${unit(quote)}`} />
        <div className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-1 text-amber-300/80">
            <AlertTriangle className="h-3 w-3" /> قیمت لیکویید
          </span>
          <span className="num font-extrabold text-amber-300" dir="ltr">
            {num(pv.liq, dg(quote))}
            <span className="ms-1 text-[10px] font-normal text-amber-300/60">
              ({fa(num(pv.liqDistancePct, 2))}٪)
            </span>
          </span>
        </div>
        <Row k="نقطه سربه‌سر" v={num(pv.breakEven, dg(quote))} />
        <Row k="کارمزد باز کردن" v={`${num(pv.openFee, dg(quote))} ${unit(quote)}`} />
        <Row k="مجموع برداشت از موجودی" v={`${num(pv.total, dg(quote))} ${unit(quote)}`} strong />
      </div>

      {showErr && (
        <p className="rounded-lg bg-down/10 px-3 py-2 text-[11px] text-down">{showErr}</p>
      )}

      {user ? (
        <Button variant={side === LONG ? 'up' : 'down'} loading={loading} disabled={!!err}
          onClick={submit} className="w-full !py-3">
          {side === LONG ? 'باز کردن موقعیت خرید' : 'باز کردن موقعیت فروش'}
        </Button>
      ) : (
        <Link to="/login" className="btn-primary w-full !py-3">ورود / ثبت‌نام</Link>
      )}

      {user && !cfg.kycOk && (
        <Link to="/kyc" className="block text-center text-[11px] text-brand-300 underline">
          برای معامله اهرمی، احراز هویت سطح {fa(cfg.minKyc)} لازم است
        </Link>
      )}

      <RiskNote cfg={cfg} />
    </div>
  )
}

function Row({ k, v, strong }: { k: string; v: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-slate-500">{k}</span>
      <span className={clsx('num', strong ? 'font-semibold text-white' : 'text-slate-300')} dir="ltr">{v}</span>
    </div>
  )
}

/* ═══════════════════════════════════════════════════ موقعیت‌ها */

export function LeveragePositions({ cfg, refreshKey }: { cfg: LevConfig; refreshKey?: number }) {
  const { user } = useAuth()
  const tickers = useMarket((s) => s.tickers)
  const [tab, setTab] = useState('open')
  const [rows, setRows] = useState<Position[] | null>(null)
  const [busy, setBusy] = useState(0)
  const [marginFor, setMarginFor] = useState<Position | null>(null)
  const [targetFor, setTargetFor] = useState<Position | null>(null)
  const timer = useRef<number | null>(null)

  const load = useCallback(() => {
    if (!user) { setRows([]); return }
    get<Position[]>(`/leverage/positions?status=${tab}`)
      .then((r) => setRows(Array.isArray(r) ? r : []))
      .catch(() => setRows([]))
  }, [user, tab])

  useEffect(load, [load, refreshKey])


  // موقعیت‌ها را دوره‌ای تازه می‌کنیم چون **سرور** ممکن است آن‌ها را ببندد
  // (لیکویید، حد سود، حد ضرر) و مرورگر از این اتفاق خبر ندارد. قیمت‌ها
  // زنده‌اند، ولی «هنوز باز است یا نه» را فقط سرور می‌داند.
  useEffect(() => {
    if (!user || tab !== 'open') return
    timer.current = window.setInterval(load, 10_000)
    return () => { if (timer.current) window.clearInterval(timer.current) }
  }, [user, tab, load])

  const act = async (p: Position, fn: () => Promise<any>, okMsg: string) => {
    setBusy(p.id)
    try { await fn(); toast.ok(okMsg); load() }
    catch (e: any) { toast.err(e.message) }
    finally { setBusy(0) }
  }

  // ⚠ پس از تمام هوک‌ها. اگر بالاتر بود، در رندرهایی که زودتر برمی‌گشت
  // تعداد هوک‌های اجراشده فرق می‌کرد و React با خطا کل صفحه را می‌انداخت.
  //
  // منطقش: وقتی اهرم خاموش است، این جدول فقط برای کسی دیده می‌شود که هنوز
  // موقعیت باز دارد. کلیدِ خاموش برای جلوگیری از ریسکِ **تازه** است؛ اگر
  // رابط کاربری هم با آن ناپدید می‌شد، کاربر هیچ راهی جز لیکویید شدن
  // نداشت — یعنی همان چیزی که در بک‌اند درست شده، اینجا از نو خراب می‌شد.
  const hasOpen = !!rows && rows.some((p) => p.status === 'open')
  const hide = !cfg.enabled && !hasOpen && tab === 'open'

  const live = (p: Position) => {
    const mark = p.status === 'open' ? (tickers[p.symbol]?.last ?? p.markPrice) : p.closePrice || p.markPrice
    const pnl = p.status === 'open' ? pnlOf(p.side, p.entryPrice, mark, p.size) : p.pnl
    return { mark, pnl, roe: roeOf(p.margin, pnl), near: p.status === 'open' && isLiquidated(p.side, mark, p.liqPrice) }
  }

  if (hide) return null

  return (
    <Card className="!p-0">
      <div className="flex flex-wrap items-center gap-2 border-b border-white/[.06] p-3">
        <h3 className="text-sm font-bold text-white">موقعیت‌های اهرمی</h3>
        {!cfg.enabled && (
          <span className="rounded-lg bg-amber-500/15 px-2 py-0.5 text-[10px] text-amber-300">
            معاملات اهرمی موقتاً بسته است — فقط می‌توانید موقعیت‌های باز را ببندید
          </span>
        )}
        <Tabs value={tab} onChange={setTab} size="sm" className="ms-auto"
          items={[{ key: 'open', label: 'باز' }, { key: 'history', label: 'تاریخچه' }]} />
      </div>

      {!user ? (
        <div className="p-6"><Empty text="برای دیدن موقعیت‌ها وارد حساب کاربری شوید" /></div>
      ) : rows === null ? (
        <div className="p-3"><Rows n={3} /></div>
      ) : rows.length === 0 ? (
        <div className="p-6"><Empty text={tab === 'open' ? 'موقعیت بازی ندارید' : 'تاریخچه‌ای وجود ندارد'} /></div>
      ) : (
        <>
        {/* ── گوشی: کارت به‌جای جدول.
            جدول یازده‌ستونه روی صفحه ۳۶۰ پیکسلی یعنی کاربر باید افقی
            بکشد تا دکمه «بستن» را ببیند — دقیقاً همان دکمه‌ای که ممکن است
            فوری لازمش داشته باشد. */}
        <div className="space-y-2 p-2 md:hidden">
          {rows.map((p) => {
            const V = live(p)
            return (
              <div key={p.id} className={clsx('rounded-xl border border-white/[.07] bg-white/[.02] p-3',
                V.near && 'border-down/40 bg-down/[.07]')}>
                <div className="mb-2 flex items-center gap-2">
                  <span className="font-bold text-white">{p.base}/{p.quote === 'IRT' ? 'TMN' : p.quote}</span>
                  <span className={clsx('rounded-md px-1.5 py-0.5 text-[10px] font-bold',
                    p.side === LONG ? 'bg-up/15 text-up' : 'bg-down/15 text-down')}>
                    {p.side === LONG ? 'خرید' : 'فروش'}
                  </span>
                  <span className="num text-[11px] text-brand-200" dir="ltr">{num(p.leverage, p.leverage % 1 ? 2 : 0)}×</span>
                  <span className={clsx('num ms-auto font-bold', V.pnl >= 0 ? 'text-up' : 'text-down')} dir="ltr">
                    {V.pnl >= 0 ? '+' : ''}{num(V.pnl, dg(p.quote))}
                    <span className="ms-1 text-[10px] font-normal">({num(V.roe, 1)}٪)</span>
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                  <Row k="وثیقه" v={num(p.margin, dg(p.quote))} />
                  <Row k="اندازه" v={num(p.size, 6)} />
                  <Row k="ورود" v={num(p.entryPrice, dg(p.quote))} />
                  <Row k={p.status === 'open' ? 'فعلی' : 'خروج'} v={num(V.mark, dg(p.quote))} />
                  {p.status === 'open'
                    ? <Row k="لیکویید" v={num(p.liqPrice, dg(p.quote))} strong />
                    : <Row k="بازگشتی" v={num(p.payout, dg(p.quote))} strong />}
                  <Row k={p.status === 'open' ? 'تاریخ' : 'وضعیت'}
                    v={p.status === 'open' ? faDate(p.createdAt, false) : closeReasonLabel(p.closeReason)} />
                </div>
                {p.status === 'open' && (
                  <div className="mt-2.5 flex gap-1.5">
                    <button onClick={() => setMarginFor(p)}
                      className="flex-1 rounded-lg border border-white/[.08] bg-white/[.03] py-2 text-[11px] text-slate-300">
                      افزودن وثیقه
                    </button>
                    <button onClick={() => setTargetFor(p)}
                      className="flex-1 rounded-lg border border-white/[.08] bg-white/[.03] py-2 text-[11px] text-slate-300">
                      حد سود / ضرر
                    </button>
                    <button disabled={busy === p.id}
                      onClick={() => act(p, () => post(`/leverage/positions/${p.id}/close`), 'موقعیت بسته شد')}
                      className="flex-1 rounded-lg bg-down/15 py-2 text-[11px] font-bold text-down disabled:opacity-50">
                      بستن
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* ── دسکتاپ: جدول */}
        <div className="hidden scroll-x md:block">
          <table className="w-full min-w-[880px] text-xs">
            <thead className="text-[10px] text-slate-500">
              <tr className="border-b border-white/[.06]">
                {['بازار', 'جهت', 'اهرم', 'وثیقه', 'اندازه', 'ورود', tab === 'open' ? 'قیمت فعلی' : 'خروج',
                  'لیکویید', 'سود / زیان', tab === 'open' ? '' : 'وضعیت', ''].map((h, i) => (
                  <th key={i} className="px-3 py-2 text-right font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const L = live(p)
                return (
                  <tr key={p.id} className={clsx('border-b border-white/[.04] last:border-0',
                    L.near && 'bg-down/[.07]')}>
                    <td className="px-3 py-2.5 font-semibold text-white">
                      {p.base}/{p.quote === 'IRT' ? 'TMN' : p.quote}
                      <div className="text-[10px] font-normal text-slate-500">{faDate(p.createdAt, false)}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={clsx('rounded-md px-1.5 py-0.5 text-[10px] font-bold',
                        p.side === LONG ? 'bg-up/15 text-up' : 'bg-down/15 text-down')}>
                        {p.side === LONG ? 'خرید' : 'فروش'}
                      </span>
                    </td>
                    <td className="num px-3 py-2.5 text-slate-300" dir="ltr">{num(p.leverage, p.leverage % 1 ? 2 : 0)}×</td>
                    <td className="num px-3 py-2.5 text-slate-300" dir="ltr">{num(p.margin, dg(p.quote))}</td>
                    <td className="num px-3 py-2.5 text-slate-400" dir="ltr">{num(p.size, 6)}</td>
                    <td className="num px-3 py-2.5 text-slate-300" dir="ltr">{num(p.entryPrice, dg(p.quote))}</td>
                    <td className="num px-3 py-2.5 text-slate-200" dir="ltr">{num(L.mark, dg(p.quote))}</td>
                    <td className="num px-3 py-2.5 text-amber-300/90" dir="ltr">{num(p.liqPrice, dg(p.quote))}</td>
                    <td className="px-3 py-2.5">
                      <div className={clsx('num font-bold', L.pnl >= 0 ? 'text-up' : 'text-down')} dir="ltr">
                        {L.pnl >= 0 ? '+' : ''}{num(L.pnl, dg(p.quote))}
                      </div>
                      <div className={clsx('num text-[10px]', L.roe >= 0 ? 'text-up/70' : 'text-down/70')} dir="ltr">
                        {L.roe >= 0 ? '+' : ''}{num(L.roe, 2)}٪
                      </div>
                    </td>
                    {tab === 'open' ? (
                      <td className="px-3 py-2.5">
                        {(p.tpPrice > 0 || p.slPrice > 0) && (
                          <div className="text-[10px] leading-4 text-slate-500">
                            {p.tpPrice > 0 && <div className="num text-up/70" dir="ltr">TP {num(p.tpPrice, dg(p.quote))}</div>}
                            {p.slPrice > 0 && <div className="num text-down/70" dir="ltr">SL {num(p.slPrice, dg(p.quote))}</div>}
                          </div>
                        )}
                      </td>
                    ) : (
                      <td className="px-3 py-2.5">
                        <Badge kind={p.status === 'liquidated' ? 'down' : 'muted'}>
                          {closeReasonLabel(p.closeReason)}
                        </Badge>
                        <div className="num mt-0.5 text-[10px] text-slate-500" dir="ltr">
                          بازگشتی {num(p.payout, dg(p.quote))}
                        </div>
                      </td>
                    )}
                    <td className="px-3 py-2.5">
                      {p.status === 'open' && (
                        <div className="flex justify-end gap-1">
                          <button onClick={() => setMarginFor(p)} title="افزودن وثیقه"
                            className="rounded-lg border border-white/[.08] bg-white/[.03] p-1.5 text-slate-400 transition hover:text-brand-200">
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => setTargetFor(p)} title="حد سود / ضرر"
                            className="rounded-lg border border-white/[.08] bg-white/[.03] px-2 py-1.5 text-[10px] text-slate-400 transition hover:text-brand-200">
                            TP/SL
                          </button>
                          <button disabled={busy === p.id}
                            onClick={() => act(p, () => post(`/leverage/positions/${p.id}/close`), 'موقعیت بسته شد')}
                            className="rounded-lg bg-down/15 px-2.5 py-1.5 text-[10px] font-bold text-down transition hover:bg-down/25 disabled:opacity-50">
                            بستن
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        </>
      )}

      <MarginModal pos={marginFor} onClose={() => setMarginFor(null)} onDone={load} cfg={cfg} />
      <TargetModal pos={targetFor} onClose={() => setTargetFor(null)} onDone={load}
        mark={targetFor ? (tickers[targetFor.symbol]?.last ?? targetFor.markPrice) : 0} />
    </Card>
  )
}

/* ── افزودن وثیقه */
function MarginModal({ pos, onClose, onDone, cfg }: {
  pos: Position | null; onClose: () => void; onDone: () => void; cfg: LevConfig
}) {
  const [v, setV] = useState('')
  const [loading, setLoading] = useState(false)
  useEffect(() => { setV('') }, [pos?.id])
  if (!pos) return null

  const amount = Number(v) || 0
  // با افزودن وثیقه، اندازه موقعیت دست نمی‌خورد؛ فقط اهرمِ مؤثر پایین
  // می‌آید و قیمت لیکویید دورتر می‌رود. همان چیزی که سرور هم می‌کند.
  const newMargin = pos.margin + amount
  const newLev = newMargin > 0 ? (pos.size * pos.entryPrice) / newMargin : pos.leverage
  // حاشیهٔ **همین موقعیت**، نه تنظیمات امروز — دقیقاً همان عددی که سرور
  // استفاده می‌کند. موقعیت‌های پیش از افزوده‌شدن این ستون، صفر دارند و
  // برایشان به تنظیمات فعلی برمی‌گردیم.
  const mmr = pos.maintenance > 0 ? pos.maintenance : cfg.maintenance
  const k = 1 / Math.max(newLev, 1e-9) - mmr
  const raw = Math.max(0, pos.side === LONG ? pos.entryPrice * (1 - k) : pos.entryPrice * (1 + k))
  // سرور قیمت لیکویید را فقط وقتی جابه‌جا می‌کند که **دورتر** برود. اگر
  // اینجا عددِ خام را نشان می‌دادیم، مودال وعده‌ای می‌داد که سرور اجرا
  // نمی‌کند.
  const farther = pos.side === LONG ? raw < pos.liqPrice : raw > pos.liqPrice
  const newLiq = pos.liqPrice > 0 && !farther ? pos.liqPrice : raw

  const submit = async () => {
    setLoading(true)
    try {
      await post(`/leverage/positions/${pos.id}/margin`, { amount })
      toast.ok('وثیقه اضافه شد')
      onDone(); onClose()
    } catch (e: any) { toast.err(e.message) } finally { setLoading(false) }
  }

  return (
    <Modal open onClose={onClose} title="افزودن وثیقه">
      <p className="mb-3 text-xs leading-6 text-slate-400">
        با افزودن وثیقه، اندازه موقعیت تغییر نمی‌کند؛ فقط اهرم مؤثر کم و
        قیمت لیکویید دورتر می‌شود.
      </p>
      <NumberInput value={v} onChange={setV} suffix={unit(pos.quote)} />
      {amount > 0 && (
        <div className="mt-3 space-y-1.5 rounded-xl border border-white/[.06] bg-ink-900/50 p-3 text-[11px]">
          <Row k="وثیقه جدید" v={`${num(newMargin, dg(pos.quote))} ${unit(pos.quote)}`} />
          <Row k="اهرم مؤثر" v={`${num(newLev, 2)}×`} />
          <Row k="قیمت لیکویید جدید" v={num(newLiq, dg(pos.quote))} strong />
        </div>
      )}
      <div className="mt-4 flex gap-2">
        <Button onClick={submit} loading={loading} disabled={amount <= 0} className="flex-1">افزودن</Button>
        <Button variant="ghost" onClick={onClose}>انصراف</Button>
      </div>
    </Modal>
  )
}

/* ── حد سود و ضرر */
function TargetModal({ pos, onClose, onDone, mark }: {
  pos: Position | null; onClose: () => void; onDone: () => void; mark: number
}) {
  const [tp, setTp] = useState('')
  const [sl, setSl] = useState('')
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    setTp(pos?.tpPrice ? String(pos.tpPrice) : '')
    setSl(pos?.slPrice ? String(pos.slPrice) : '')
  }, [pos?.id])
  if (!pos) return null

  const a = Number(tp) || 0
  const b = Number(sl) || 0
  // از همان تابع مشترکی که فرم باز کردن هم استفاده می‌کند — تا دو مسیرِ
  // رابط کاربری نتوانند از هم و از سرور فاصله بگیرند. بررسیِ «آن‌سوی قیمت
  // لیکویید نباشد» هم همان‌جاست.
  const bad = checkTargets(pos.side, mark, pos.liqPrice, a, b)

  const gain = a ? pnlOf(pos.side, pos.entryPrice, a, pos.size) : 0
  const loss = b ? pnlOf(pos.side, pos.entryPrice, b, pos.size) : 0

  const submit = async () => {
    setLoading(true)
    try {
      await post(`/leverage/positions/${pos.id}/targets`, { tp: a, sl: b })
      toast.ok('حد سود و ضرر ثبت شد')
      onDone(); onClose()
    } catch (e: any) { toast.err(e.message) } finally { setLoading(false) }
  }

  return (
    <Modal open onClose={onClose} title="حد سود و حد ضرر">
      <div className="mb-2 text-[11px] text-slate-500">
        قیمت فعلی <span className="num text-slate-300" dir="ltr">{num(mark, dg(pos.quote))}</span>
        {' · '}لیکویید <span className="num text-amber-300" dir="ltr">{num(pos.liqPrice, dg(pos.quote))}</span>
      </div>
      <label className="label">حد سود</label>
      <NumberInput value={tp} onChange={setTp} suffix={unit(pos.quote)} placeholder="بدون حد سود" />
      {a > 0 && <p className="mt-1 text-[11px] text-up">سود تقریبی {num(gain, dg(pos.quote))} {unit(pos.quote)} ({fa(num(roeOf(pos.margin, gain), 1))}٪)</p>}
      <label className="label mt-3">حد ضرر</label>
      <NumberInput value={sl} onChange={setSl} suffix={unit(pos.quote)} placeholder="بدون حد ضرر" />
      {b > 0 && <p className="mt-1 text-[11px] text-down">زیان تقریبی {num(loss, dg(pos.quote))} {unit(pos.quote)} ({fa(num(roeOf(pos.margin, loss), 1))}٪)</p>}
      {bad && <p className="mt-3 rounded-lg bg-down/10 px-3 py-2 text-[11px] text-down">{bad}</p>}
      <div className="mt-4 flex gap-2">
        <Button onClick={submit} loading={loading} disabled={!!bad} className="flex-1">ثبت</Button>
        <Button variant="ghost" onClick={() => { setTp(''); setSl('') }}>پاک کردن</Button>
      </div>
    </Modal>
  )
}

/* ═══════════════════════════════════════════════════ خلاصه */

export function LeverageSummary({ refreshKey }: { refreshKey?: number }) {
  const { user } = useAuth()
  const [s, setS] = useState<any>(null)
  useEffect(() => {
    if (!user) return
    get('/leverage/summary').then(setS).catch(() => setS(null))
  }, [user, refreshKey])
  if (!s || !s.open) return null
  return (
    <div className="scroll-x gap-5 rounded-2xl border border-white/[.07] bg-white/[.03] px-4 py-2.5 text-[11px]">
      {[
        ['موقعیت باز', fa(s.open)],
        ['وثیقه درگیر', `${num(s.marginIrt)} تومان`],
        ['ارزش کل', `${num(s.notionalIrt)} تومان`],
        ['سود/زیان باز', `${s.unrealizedIrt >= 0 ? '+' : ''}${num(s.unrealizedIrt)}`],
      ].map(([k, v], i) => (
        <div key={i} className="shrink-0">
          <div className="text-slate-500">{k}</div>
          <div className={clsx('num font-semibold',
            i === 3 ? (s.unrealizedIrt >= 0 ? 'text-up' : 'text-down') : 'text-slate-200')}>{v}</div>
        </div>
      ))}
    </div>
  )
}
